/**
 * Telling a damaged .dcm apart from one that simply has no image (CAB-476).
 *
 * Both fail to render, so the viewer cannot distinguish them by trying — but
 * the bytes can. A structurally valid DICOM with no PixelData is either a
 * genuine non-image object (structured report, RT plan, an encapsulated PDF
 * report) or an image study that was truncated in transit.
 *
 * The rule mirrors `extractor/app/core/engines/dicom.py`, so the Documents
 * page and Oliver's chat reach the same verdict on the same file.
 */
import dicomParser from "dicom-parser";

import { getFileExtension } from "./fileHelpers";

/** A readable image study — hand it to the viewer. */
export const DICOM_IMAGE = "image";
/** Valid, but carries no image by design. */
export const DICOM_NO_IMAGE = "no-image";
/** Not a DICOM, or damaged/truncated. */
export const DICOM_UNREADABLE = "unreadable";

// Keyed on the SOP class rather than Modality: the SOP class is the object's
// authoritative type declaration and it sits in the file-meta group at the head
// of the file, so it survives a truncation that drops Modality. Modality is
// also too coarse — an encapsulated PDF report carries "DOC" or the wholly
// ambiguous "OT", which also labels genuine images.

/** Branches containing nothing but non-image objects. */
const NON_IMAGE_SOP_CLASS_PREFIXES = [
  "1.2.840.10008.5.1.4.1.1.9.", // waveforms (ECG, hemodynamic, ...)
  "1.2.840.10008.5.1.4.1.1.11.", // presentation states
  "1.2.840.10008.5.1.4.1.1.88.", // structured reports, incl. key object selection
  "1.2.840.10008.5.1.4.1.1.104.", // encapsulated documents (PDF, CDA, STL, OBJ, MTL)
];

/**
 * Branches that mix the two — RT Image, RT Dose and Segmentation all carry
 * pixels — so their pixel-less members are listed one by one.
 */
const NON_IMAGE_SOP_CLASSES = new Set([
  "1.2.840.10008.5.1.4.1.1.66.1", // spatial registration
  "1.2.840.10008.5.1.4.1.1.66.2", // spatial fiducials
  "1.2.840.10008.5.1.4.1.1.66.3", // deformable spatial registration
  "1.2.840.10008.5.1.4.1.1.67", // real world value mapping
  "1.2.840.10008.5.1.4.1.1.481.3", // RT structure set
  "1.2.840.10008.5.1.4.1.1.481.5", // RT plan
  "1.2.840.10008.5.1.4.1.1.481.6", // RT brachy treatment record
  "1.2.840.10008.5.1.4.1.1.481.7", // RT treatment summary record
  "1.2.840.10008.5.1.4.1.1.481.8", // RT ion plan
  "1.2.840.10008.5.1.4.1.1.481.9", // RT ion beams treatment record
]);

// All three carry an image. Cornerstone reads the float variants too, so
// treating them as "no image" would hide a study the viewer can actually show.
const PIXEL_DATA_TAGS = [
  "x7fe00010", // PixelData
  "x7fe00008", // FloatPixelData
  "x7fe00009", // DoubleFloatPixelData
];
const MEDIA_STORAGE_SOP_CLASS_TAG = "x00020002";
const SOP_CLASS_TAG = "x00080016";

const DICOM_EXTENSIONS = [".dcm", ".dicom"];

/** Routing is by extension everywhere else in the stack; match it here. */
export function isDicomFileName(name) {
  return DICOM_EXTENSIONS.includes(getFileExtension(String(name || "")));
}

function isNonImageSopClass(sopClass) {
  return (
    NON_IMAGE_SOP_CLASSES.has(sopClass) ||
    NON_IMAGE_SOP_CLASS_PREFIXES.some((prefix) => sopClass.startsWith(prefix))
  );
}

/**
 * Classify DICOM bytes as one of the three `DICOM_*` verdicts.
 *
 * @param {ArrayBuffer|Uint8Array} bytes The whole file.
 * @returns {string} One of DICOM_IMAGE, DICOM_NO_IMAGE, DICOM_UNREADABLE.
 */
export function inspectDicom(bytes) {
  let dataSet;
  try {
    // Parsed to the end deliberately, not stopped at PixelData: it is reading
    // past the pixel element's declared length that catches a study truncated
    // mid-transfer, the most common way one of these files is damaged. The
    // walk only indexes elements — it never decodes the pixels.
    dataSet = dicomParser.parseDicom(
      bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
    );
  } catch (err) {
    // parseDicom rejects with a plain object in some paths, so nothing here
    // may assume an Error.
    return DICOM_UNREADABLE;
  }

  if (PIXEL_DATA_TAGS.some((tag) => dataSet.elements[tag])) {
    return DICOM_IMAGE;
  }

  const sopClass = (
    dataSet.string(MEDIA_STORAGE_SOP_CLASS_TAG) ||
    dataSet.string(SOP_CLASS_TAG) ||
    ""
  )
    .replace(/\0/g, "")
    .trim();

  return isNonImageSopClass(sopClass) ? DICOM_NO_IMAGE : DICOM_UNREADABLE;
}

/** The message a clinician sees when a .dcm is refused. */
export const DICOM_REJECTED_MESSAGE =
  "This file could not be read as a valid DICOM image and was not uploaded. " +
  "It appears to be damaged or incomplete.";

export const MAX_DICOM_PREFLIGHT_BYTES = 200 * 1024 * 1024;

/**
 * Reject a damaged .dcm before it is stored, so the Documents page and Oliver's
 * chat agree on the same file. A pixel-less-by-design object is allowed
 * through: the viewer shows a dedicated "no image" state for it.
 *
 * @param {File} file The file about to be uploaded.
 * @throws {Error} With a user-facing message when the file is unreadable.
 */
export async function assertUploadableDicom(file) {
  if (!isDicomFileName(file?.name)) {
    return;
  }

  if (file.size > MAX_DICOM_PREFLIGHT_BYTES) {
    return;
  }

  let verdict;
  try {
    verdict = inspectDicom(await file.arrayBuffer());
  } catch (err) {
    // Could not read the file off disk at all — that is not a verdict on its
    // contents, so let the upload through rather than block on our own
    // failure. Oliver's ingestion still refuses to index unreadable bytes.
    return;
  }

  if (verdict === DICOM_UNREADABLE) {
    throw new Error(DICOM_REJECTED_MESSAGE);
  }
}
